import React, { useState, useEffect, useRef } from 'react';
import { axiosInstance } from '@/App';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Search, X, Phone, Mail, User } from 'lucide-react';
import { motivoDeCoincidencia } from '@/lib/coincidencia';

/**
 * Componente de busqueda de clientes con busqueda SERVER-SIDE debounced.
 * Motivo del cambio: con 1500+ clientes la version anterior cargaba solo la
 * primera pagina (50) y filtraba en JS, asi que los clientes de paginas
 * posteriores no aparecian al buscar. Ahora llamamos a /customers?search=X
 * tras 300ms de inactividad y mostramos los resultados del backend.
 */
export function ClientSearchSelect({ value, onValueChange, label = "Cliente",
                                    required = false, placeholder = null,
                                    // Texto para "sin cliente". Solo lo usan
                                    // los filtros: en un formulario que exige
                                    // cliente no se pasa.
                                    textoVacio = null }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredClients, setFilteredClients] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  // Cuando cambia `value` desde fuera (p.ej. al editar un presupuesto existente),
  // hacemos una peticion puntual para recuperar ese cliente y mostrarlo.
  useEffect(() => {
    if (!value) {
      setSelectedClient(null);
      setSearchQuery('');
      return;
    }
    // Si ya tenemos seleccionado ese id, no recargamos.
    if (selectedClient && selectedClient.id === value) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await axiosInstance.get(`/customers/${value}`);
        if (!cancelled && resp?.data) {
          setSelectedClient(resp.data);
          setSearchQuery(resp.data.name || '');
        }
      } catch (e) {
        // Cliente borrado o sin permisos: limpiamos.
        if (!cancelled) {
          setSelectedClient(null);
          setSearchQuery('');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [value]);

  // Debounced server-side search mientras el usuario teclea.
  useEffect(() => {
    if (selectedClient) return; // ya hay uno seleccionado, el input esta oculto
    if (searchQuery.length < 2) {
      setFilteredClients([]);
      setShowResults(false);
      setIsSearching(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setIsSearching(true);

    debounceRef.current = setTimeout(async () => {
      try {
        const resp = await axiosInstance.get('/customers', {
          params: { search: searchQuery.trim(), page_size: 20 },
          signal: controller.signal,
        });
        const items = resp?.data?.items || resp?.data || [];
        setFilteredClients(items);
        setShowResults(true);
      } catch (e) {
        if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
        setFilteredClients([]);
        setShowResults(true);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery, selectedClient]);

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setSearchQuery(client.name);
    setShowResults(false);
    // Se devuelve tambien la ficha entera: quien necesita el nombre o los
    // datos fiscales no tiene que volver a pedirla al servidor. Quien solo
    // quiere el id sigue funcionando igual.
    onValueChange(client.id, client);
  };

  const handleClearClient = () => {
    setSelectedClient(null);
    setSearchQuery('');
    setFilteredClients([]);
    setShowResults(false);
    onValueChange(null, null);
  };

  return (
    <div className="space-y-2 relative">
      {label && <Label htmlFor="client-search">{label} {required && <span className="text-red-500">*</span>}</Label>}

      {selectedClient ? (
        <div className="relative">
          <div className="flex items-center gap-2 p-3 border rounded-md bg-blue-50 border-blue-200">
            <User className="w-4 h-4 text-blue-600" />
            <div className="flex-1">
              <div className="font-medium text-sm text-blue-900">{selectedClient.name}</div>
              {selectedClient.email && (
                <div className="text-xs text-blue-700">{selectedClient.email}</div>
              )}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 hover:bg-blue-100"
              onClick={handleClearClient}
              title="Cambiar cliente"
            >
              <X className="w-4 h-4 text-blue-600" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            id="client-search"
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => searchQuery.length >= 2 && setShowResults(true)}
            placeholder={placeholder || textoVacio
              || "Buscar por nombre, email, tel, NIF, razón social..."}
            className="pl-10"
            required={required}
            autoComplete="off"
          />

          {showResults && filteredClients.length > 0 && (
            <Card className="absolute z-50 w-full mt-1 max-h-80 overflow-y-auto shadow-lg">
              <div className="p-2 space-y-1">
                {filteredClients.map((client) => (
                  <div
                    key={client.id}
                    onClick={() => handleSelectClient(client)}
                    className="p-3 hover:bg-gray-100 rounded cursor-pointer transition-colors"
                  >
                    <div className="font-semibold flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-500" />
                      {client.name}
                    </div>
                    {/* Por que ha salido este. El servidor busca por siete
                        campos y tres no se ven en la fila (NIF, telefono,
                        email de plataforma): sin decirlo, buscar un NIF
                        devuelve un nombre que no se parece a lo escrito y
                        parece que el buscador ha fallado. */}
                    {motivoDeCoincidencia(client, searchQuery) && (
                      <div className="text-xs text-amber-700 mt-0.5">
                        {motivoDeCoincidencia(client, searchQuery)}
                      </div>
                    )}
                    <div className="text-sm text-gray-600 mt-1 space-y-1">
                      {client.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="w-3 h-3" />
                          {client.email}
                        </div>
                      )}
                      {client.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-3 h-3" />
                          {client.phone}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {showResults && searchQuery.length >= 2 && filteredClients.length === 0 && !isSearching && (
            <Card className="absolute z-50 w-full mt-1 shadow-lg">
              <div className="p-4 text-center text-gray-500">
                No se encontraron clientes
              </div>
            </Card>
          )}

          {isSearching && (
            <Card className="absolute z-50 w-full mt-1 shadow-lg">
              <div className="p-3 text-sm text-gray-500">Buscando...</div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
